import React, { useState, useEffect } from "react";
import { apiClient } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../components/ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { 
  BarChart3,
  Eye,
  MousePointerClick,
  AlertTriangle,
  Mail,
  TrendingUp
} from "lucide-react";

const COLORS = ["#10b981", "#3b82f6", "#ef4444"];

export const AnalyticsPage = () => {
  const [overview, setOverview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [overviewRes, campaignsRes] = await Promise.all([
        apiClient.get("/analytics/overview"),
        apiClient.get("/analytics/campaigns"),
      ]);
      setOverview(overviewRes.data);
      setCampaigns(campaignsRes.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
      </div>
    );
  }

  const pieData = overview ? [
    { name: "Opens", value: overview.total_opens || 0 },
    { name: "Clicks", value: overview.total_clicks || 0 },
    { name: "Bounces", value: overview.total_bounces || 0 },
  ] : [];

  const barData = campaigns.map(c => ({
    name: c.name.length > 15 ? c.name.substring(0, 15) + "..." : c.name,
    sent: c.sent_count,
    opens: c.open_count,
    clicks: c.click_count,
  }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
      <div>
        <h1 className="text-3xl font-bold font-heading tracking-tight text-slate-900">Analytics</h1>
        <p className="text-slate-500 mt-1">Track your email campaign performance</p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Sent</p>
                <p className="text-3xl font-bold font-heading mt-1 text-slate-900">
                  {overview?.total_sent || overview?.emails?.sent || 0}
                </p>
              </div>
              <div className="w-10 h-10 bg-slate-100 rounded-md flex items-center justify-center">
                <Mail className="w-5 h-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Open Rate</p>
                <p className="text-3xl font-bold font-heading mt-1 text-emerald-600">
                  {overview?.open_rate || 0}%
                </p>
              </div>
              <div className="w-10 h-10 bg-emerald-100 rounded-md flex items-center justify-center">
                <Eye className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Click Rate</p>
                <p className="text-3xl font-bold font-heading mt-1 text-blue-600">
                  {overview?.click_rate || 0}%
                </p>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-md flex items-center justify-center">
                <MousePointerClick className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Bounce Rate</p>
                <p className="text-3xl font-bold font-heading mt-1 text-red-600">
                  {overview?.bounce_rate || 0}%
                </p>
              </div>
              <div className="w-10 h-10 bg-red-100 rounded-md flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Campaign Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {barData.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-slate-400">
                No campaign data yet
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#64748b" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#64748b" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: "#fff", 
                      border: "1px solid #e2e8f0",
                      borderRadius: "6px"
                    }} 
                  />
                  <Bar dataKey="sent" fill="#1e293b" name="Sent" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="opens" fill="#10b981" name="Opens" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="clicks" fill="#3b82f6" name="Clicks" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Pie Chart */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Engagement Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.every(d => d.value === 0) ? (
              <div className="flex items-center justify-center h-64 text-slate-400">
                No engagement data yet
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: "#fff", 
                      border: "1px solid #e2e8f0",
                      borderRadius: "6px"
                    }} 
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Campaign Details Table */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-200">
          <CardTitle className="font-heading">Campaign Details</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {campaigns.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-slate-400">
              No sent campaigns yet
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="font-semibold text-slate-600">Campaign</TableHead>
                  <TableHead className="font-semibold text-slate-600 text-right">Sent</TableHead>
                  <TableHead className="font-semibold text-slate-600 text-right">Opens</TableHead>
                  <TableHead className="font-semibold text-slate-600 text-right">Open Rate</TableHead>
                  <TableHead className="font-semibold text-slate-600 text-right">Clicks</TableHead>
                  <TableHead className="font-semibold text-slate-600 text-right">Click Rate</TableHead>
                  <TableHead className="font-semibold text-slate-600 text-right">Bounces</TableHead>
                  <TableHead className="font-semibold text-slate-600 text-right">Bounce Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((campaign) => (
                  <TableRow key={campaign.id} className="table-row-hover" data-testid={`analytics-row-${campaign.id}`}>
                    <TableCell className="font-medium text-slate-900">{campaign.name}</TableCell>
                    <TableCell className="text-right text-slate-600">{campaign.sent_count}</TableCell>
                    <TableCell className="text-right text-emerald-600 font-medium">{campaign.open_count}</TableCell>
                    <TableCell className="text-right text-slate-600">{campaign.open_rate}%</TableCell>
                    <TableCell className="text-right text-blue-600 font-medium">{campaign.click_count}</TableCell>
                    <TableCell className="text-right text-slate-600">{campaign.click_rate}%</TableCell>
                    <TableCell className="text-right text-red-600 font-medium">{campaign.bounce_count}</TableCell>
                    <TableCell className="text-right text-slate-600">{campaign.bounce_rate}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
